import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Turnstile, type TurnstileInstance} from '@marsidev/react-turnstile';
import {
  ensureTenantCookieFromQuery,
  getTenantDisplayName,
  resendVerificationCode,
  verifyEmail
} from '../services/authService';
import {TURNSTILE_SITE_KEY} from '../config/env';
import './EmailVerification.css';

const RESEND_COOLDOWN = 60;

/**
 * Parse the expiration-date param sent in the verification link.
 * The backend uses '*' as separator between date and time instead of 'T',
 * e.g. "2026-03-16*17:22:35.255792+00:00".
 * Returns a Date object; returns null if parsing fails.
 */
function parseExpirationDate(raw: string): Date | null {
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
  if (!m) return null;

  const [, yS, moS, dS, hS, miS, sS] = m;
  const y = Number(yS);
  const mo = Number(moS) - 1; // Date month is 0-based
  const day = Number(dS);
  const h = Number(hS);
  const mi = Number(miS);
  const s = Number(sS);

  // Rango básico
  if (mo < 0 || mo > 11 || h < 0 || h > 23 || mi < 0 || mi > 59 || s < 0 || s > 59 || day < 1) {
    return null;
  }

  // Construir fecha en UTC y validar desbordes (ej. 2026-02-30)
  const dateUtc = new Date(Date.UTC(y, mo, day, h, mi, s, 0));
  if (
      dateUtc.getUTCFullYear() !== y ||
      dateUtc.getUTCMonth() !== mo ||
      dateUtc.getUTCDate() !== day ||
      dateUtc.getUTCHours() !== h ||
      dateUtc.getUTCMinutes() !== mi ||
      dateUtc.getUTCSeconds() !== s
  ) {
    return null;
  }

  // Restar 3 horas (GMT+0 → GMT-3)
  return new Date(dateUtc.getTime());
}

type VerifyState = 'verifying' | 'verified' | 'error' | 'expired';

function EmailVerification() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const email = params.get('email') ?? '';
  const code = params.get('code') ?? '';
  const rawExpiration = params.get('expiration-date') ?? '';

  // Ensure the CLINARYA_SUBDOMAIN cookie is set from the URL param when missing.
  // Lazy initializer runs once before the first render so that getTenantDisplayName()
  // below already sees the freshly-created cookie.
  const [tenant] = useState<string | null>(() => ensureTenantCookieFromQuery(location.search));

  const tenantName = getTenantDisplayName();

  const expirationDate = parseExpirationDate(rawExpiration);
  const isExpired = !expirationDate || expirationDate <= new Date();

  const [verifyState, setVerifyState] = useState<VerifyState>(isExpired ? 'expired' : 'verifying');
  const [verifyError, setVerifyError] = useState('');
  const [contextError, setContextError] = useState('');

  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance>(null);

  const verifyDone = useRef(false);

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken('');
  };

  // Countdown timer: decrement every second while cooldown is active
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  // Auto-verify when not expired and required params are available
  useEffect(() => {
    if (isExpired) return;
    if (verifyDone.current) return;
    if (!email || !code) {
      navigate('/register');
      return;
    }
    if (!tenant) {
      setContextError(
        'No se encontró el contexto de la empresa. Por favor abra el link desde el correo original o solicite uno nuevo.',
      );
      setVerifyState('error');
      return;
    }
    verifyDone.current = true;
    verifyEmail({ email, code, turnstile_token: turnstileToken })
      .then(() => {
        setVerifyState('verified');
        setTimeout(() => navigate('/login'), 5000);
      })
      .catch((err: unknown) => {
        console.error('Verify error:', err);
        setVerifyState('error');
        setVerifyError(err instanceof Error ? err.message : 'Error al verificar el email.');
      });
  // Intentionally run once on mount; isExpired/email/code are derived from
  // URL params which do not change during the component lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (cooldownSeconds > 0) return;
    if (!turnstileToken) {
      setResendError('Por favor complete la verificación de seguridad');
      return;
    }
    setResending(true);
    setResendError('');
    setResendSuccess(false);
    try {
      await resendVerificationCode({ email, turnstile_token: turnstileToken });
      setResendSuccess(true);
      setCooldownSeconds(RESEND_COOLDOWN);
      resetTurnstile();
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      console.error('Resend error:', err);
      setResendError(err instanceof Error ? err.message : 'Error al reenviar el email. Por favor, intente nuevamente.');
      resetTurnstile();
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-box">
        <div className="verification-header">
          <h1 className="verification-title">{tenantName}</h1>
          <h2 className="verification-subtitle">Verificación de email</h2>
        </div>

        {verifyState === 'verifying' && (
          <p className="verification-text verification-loading">Verificando...</p>
        )}

        {verifyState === 'verified' && (
          <div className="verification-result verification-success">
            <p>El usuario ha sido validado</p>
            <p className="verification-redirect-hint">Será redirigido al login en 5 segundos...</p>
          </div>
        )}

        {verifyState === 'error' && (
          <div className="verification-result verification-error-state">
            <p>{contextError || verifyError || 'Error al verificar el email.'}</p>
          </div>
        )}

        {verifyState === 'expired' && (
          <div className="verification-result verification-expired">
            {!tenant ? (
              <p className="error-message">
                No se encontró el contexto de la empresa. Por favor abra el link desde el correo original o solicite uno nuevo.
              </p>
            ) : (
              <>
                <p>Su usuario no se encuentra verificado</p>

                {resendError && <div className="error-message">{resendError}</div>}
                {resendSuccess && <div className="success-message">Email de verificación reenviado exitosamente</div>}

                <div className="form-group turnstile-wrapper">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setTurnstileToken('')}
                    onExpire={() => setTurnstileToken('')}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || cooldownSeconds > 0}
                  className="verify-button"
                >
                  {resending
                    ? 'Reenviando...'
                    : cooldownSeconds > 0
                    ? `reenvio de e-mail de verificacion (${cooldownSeconds}s)`
                    : 'reenvio de e-mail de verificacion'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailVerification;
